import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  TimeScale,
  Tooltip,
  type TooltipItem,
} from 'chart.js';
import 'chartjs-adapter-date-fns';
import zoomPlugin from 'chartjs-plugin-zoom';
import { useRef, useMemo, useCallback } from 'react';
import { Bar } from 'react-chartjs-2';

// Register Chart.js components for bar charts
ChartJS.register(CategoryScale, LinearScale, BarElement, TimeScale, Tooltip, zoomPlugin);

export interface HistogramDataset {
  label: string;
  data: Array<{ timestamp: string; value: number | null }>;
  color?: string;
}

export interface HistogramChartProps {
  /** Single dataset (legacy) */
  data: Array<{ timestamp: string; value: number | null }>;
  /** Multiple datasets for stacked bars */
  datasets?: HistogramDataset[];
  /** Whether to stack multiple datasets (default: true) */
  stacked?: boolean;
  /** Bucket size for timestamp alignment in stacked mode */
  bucketSize?: '1h' | '1d' | '1w' | '1M';
  startTime: string;
  endTime: string;
  unit?: string;
  color?: string;
  height?: number;
  isLoading?: boolean;
  onZoom?: (start: Date, end: Date) => void;
  onResetZoom?: () => void;
}

// Balanced palette: calm for dark mode but with sufficient contrast
const DEFAULT_CHART_COLORS = [
  '#6A9FD4', // Calm blue - clear primary
  '#D4A56A', // Muted ochre - warm contrast without being aggressive
  '#8FBCB0', // Soft teal
  '#B8A0C8', // Dusty lavender
  '#9CB894', // Sage green
  '#C9A090', // Dusty coral
  '#7AA8B8', // Steel blue
  '#C4B090', // Warm sand
];

// Use the first palette color for single dataset mode instead of gray
const DEFAULT_CHART_COLOR = DEFAULT_CHART_COLORS[0];

export function HistogramChart({
  data,
  datasets,
  stacked = true,
  bucketSize = '1d',
  startTime,
  endTime,
  unit,
  color,
  height,
  isLoading,
  onZoom,
  onResetZoom,
}: HistogramChartProps) {
  // When height is undefined, use 100% to fill the container
  const heightStyle = height !== undefined ? { height } : { height: '100%' };
  const chartRef = useRef<ChartJS<'bar', { x: number; y: number }[]>>(null);

  const chartColor = color || DEFAULT_CHART_COLOR;
  const hasMultipleDatasets = datasets && datasets.length > 0;

  // Handle zoom complete - called by the zoom plugin
  const handleZoomComplete = useCallback(
    ({ chart }: { chart: ChartJS }) => {
      if (!onZoom) return;

      const xScale = chart.scales['x'];
      if (!xScale) return;

      const min = xScale.min;
      const max = xScale.max;

      if (min !== undefined && max !== undefined && max - min > 1000) {
        onZoom(new Date(min), new Date(max));
      }
    },
    [onZoom]
  );

  // Handle double-click to reset
  const handleDoubleClick = useCallback(() => {
    if (onResetZoom) {
      onResetZoom();
    }
  }, [onResetZoom]);

  // Convert data to Chart.js format
  const chartData = useMemo(() => {
    if (hasMultipleDatasets && datasets) {
      // Multiple datasets - stacked bars
      // Round timestamps AND aggregate values that fall into the same bucket
      // This handles cases where the API returns multiple data points per bucket
      const getBucketTimestamp = (timestamp: string): number => {
        const date = new Date(timestamp);
        if (bucketSize === '1h') {
          // Round to start of local hour
          return new Date(
            date.getFullYear(),
            date.getMonth(),
            date.getDate(),
            date.getHours()
          ).getTime();
        }
        if (bucketSize === '1w') {
          // Round to start of local week (Monday)
          const dayOfWeek = date.getDay();
          const daysToMonday = dayOfWeek === 0 ? 6 : dayOfWeek - 1; // Sunday = 6 days back
          const monday = new Date(
            date.getFullYear(),
            date.getMonth(),
            date.getDate() - daysToMonday
          );
          return monday.getTime();
        }
        if (bucketSize === '1M') {
          // Round to start of local month
          return new Date(date.getFullYear(), date.getMonth(), 1).getTime();
        }
        // Round to start of local day (default for 1d buckets)
        return new Date(date.getFullYear(), date.getMonth(), date.getDate()).getTime();
      };

      // Aggregate data points that fall into the same bucket (sum them)
      const aggregateByBucket = (
        dataPoints: Array<{ timestamp: string; value: number | null }>
      ): Array<{ x: number; y: number }> => {
        const bucketMap = new Map<number, number>();
        for (const point of dataPoints) {
          const bucketTs = getBucketTimestamp(point.timestamp);
          const existing = bucketMap.get(bucketTs) ?? 0;
          bucketMap.set(bucketTs, existing + (point.value ?? 0));
        }
        // Convert to array sorted by timestamp
        return Array.from(bucketMap.entries())
          .sort((a, b) => a[0] - b[0])
          .map(([x, y]) => ({ x, y }));
      };

      return {
        datasets: datasets.map((dataset, index) => {
          const dsColor =
            dataset.color || DEFAULT_CHART_COLORS[index % DEFAULT_CHART_COLORS.length];
          return {
            label: dataset.label,
            data: aggregateByBucket(dataset.data),
            // Use full opacity for stacked bars to avoid rendering artifacts
            backgroundColor: stacked ? dsColor : `${dsColor}cc`,
            // No borders for stacked bars - prevents visual artifacts at bar boundaries
            borderColor: stacked ? 'transparent' : dsColor,
            borderWidth: stacked ? 0 : 1,
            borderRadius: stacked ? 0 : 2,
            stack: stacked ? 'stack0' : undefined,
          };
        }),
      };
    }

    // Single dataset (legacy behavior)
    return {
      datasets: [
        {
          data: data.map((point) => ({
            x: new Date(point.timestamp).getTime(),
            y: point.value ?? 0,
          })),
          backgroundColor: `${chartColor}cc`,
          borderColor: chartColor,
          borderWidth: 1,
          borderRadius: 2,
        },
      ],
    };
  }, [data, datasets, hasMultipleDatasets, stacked, bucketSize, chartColor]);

  // Chart options - memoized with time bounds
  const options = useMemo(() => {
    const startMs = new Date(startTime).getTime();
    const endMs = new Date(endTime).getTime();
    const isDark = document.documentElement.classList.contains('dark');
    const gridColor = isDark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.06)';
    const textColor = isDark ? '#a1a1aa' : '#6b7280';

    return {
      responsive: true,
      maintainAspectRatio: false,
      animation: false as const,
      layout: {
        padding: {
          left: 8,
        },
      },
      scales: {
        x: {
          type: 'time' as const,
          min: startMs,
          max: endMs,
          grid: {
            color: gridColor,
          },
          ticks: {
            color: textColor,
            font: { size: 11 },
            maxRotation: 0,
            autoSkip: true,
            maxTicksLimit: 12,
          },
          offset: true,
          stacked: hasMultipleDatasets && stacked,
        },
        y: {
          // Don't force zero - allow negative values to display properly
          grid: {
            color: gridColor,
          },
          ticks: {
            color: textColor,
            font: { size: 11 },
            callback: function (
              this: { ticks?: Array<{ value: number }> },
              value: number | string
            ) {
              const v = Number(value);
              if (Number.isInteger(v)) return String(v);

              // Determine appropriate decimal places based on tick step size
              const ticks = this.ticks;
              if (ticks && ticks.length >= 2 && ticks[0] && ticks[1]) {
                const step = Math.abs(ticks[1].value - ticks[0].value);
                if (step > 0 && step < 1) {
                  // Calculate decimals needed: -floor(log10(step))
                  const decimals = Math.min(6, Math.max(1, Math.ceil(-Math.log10(step))));
                  return v.toFixed(decimals);
                }
              }
              return v.toFixed(1);
            },
          },
          stacked: hasMultipleDatasets && stacked,
        },
      },
      plugins: {
        legend: {
          display: hasMultipleDatasets,
          position: 'top' as const,
          labels: {
            boxWidth: 12,
            padding: 8,
            font: { size: 11 },
            color: textColor,
          },
        },
        tooltip: {
          enabled: true,
          mode: 'index' as const,
          intersect: false,
          callbacks: {
            label: function (context: TooltipItem<'bar'>) {
              const label = context.dataset.label || '';
              const v = context.parsed.y ?? 0;
              const suffix = unit ? ` ${unit}` : '';
              return hasMultipleDatasets
                ? `${label}: ${v.toFixed(2)}${suffix}`
                : `${v.toFixed(2)}${suffix}`;
            },
          },
        },
        zoom: {
          zoom: {
            drag: {
              enabled: true,
              backgroundColor: isDark ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.1)',
            },
            mode: 'x' as const,
            onZoomComplete: handleZoomComplete,
          },
        },
      },
      interaction: {
        mode: 'index' as const,
        intersect: false,
      },
    };
  }, [startTime, endTime, unit, hasMultipleDatasets, stacked, handleZoomComplete]);

  if (isLoading) {
    return (
      <div
        style={heightStyle}
        className="flex items-center justify-center rounded-lg bg-gray-50 dark:bg-zinc-900"
      >
        <div className="h-6 w-6 animate-spin rounded-full border-2 border-gray-300 border-t-blue-500 dark:border-zinc-700" />
      </div>
    );
  }

  // Create a key that forces chart recreation when time bounds change
  // Chart.js doesn't always properly update scale min/max values
  const chartKey = `${startTime}-${endTime}-${bucketSize}`;

  return (
    <div className="relative h-full">
      <div style={heightStyle} onDoubleClick={handleDoubleClick}>
        <Bar key={chartKey} ref={chartRef} data={chartData} options={options} />
      </div>
      {((hasMultipleDatasets && datasets?.every((ds) => ds.data.length === 0)) ||
        (!hasMultipleDatasets && data.length === 0)) && (
        <div className="absolute inset-0 flex items-center justify-center">
          <p className="text-sm text-gray-500 dark:text-zinc-500">No data available</p>
        </div>
      )}
    </div>
  );
}
